import { Dropdown } from 'react-bootstrap';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { ProductSetType } from '@econtractor/types';
import * as apiProductSet from 'api/apiProductSets';
import { useToast } from 'context/ToastContext';
import SvgEdit from 'components/svg/SvgEdit';
import SvgTrash from 'components/svg/SvgTrash';
import { useProductSet } from './context/ProductSetProvider';
import useDeleteProductSet from './hooks/useDeleteProductSet';
import SolarIcon from 'components/icons/SolarIcon';

interface SetTableRowActionsProps {
    productSet: ProductSetType;
    fetchSets: (force?: boolean) => Promise<void>;
    setEditSetId: (setId: number) => void;
}

const SetTableRowActions = ({ productSet, fetchSets, setEditSetId }: SetTableRowActionsProps) => {
    const { t } = useTranslation(['Sets', 'common']);
    const { showToast } = useToast();
    const { setShowSpinner } = useProductSet();
    const { deleteProductSet } = useDeleteProductSet();

    const cbDeleteSet = async () => {
        setShowSpinner(true);
        const resDelete = await apiProductSet.deleteProductSet(productSet.ID);
        await fetchSets(true);
        setShowSpinner(false);

        if (resDelete) showToast(t('messSetDeleted'), 'success');
        else showToast(t('common:messError'), 'danger');
    };

    const renderMenuItem = (
        icon: React.ReactNode,
        title: string,
        subtitle: string,
        onSelect: () => void,
        options?: { danger?: boolean },
    ) => (
        <Dropdown.Item
            as="button"
            className={options?.danger ? 'text-danger' : undefined}
            onClick={(e) => {
                onSelect();
                e.currentTarget.blur();
            }}
        >
            <div className="d-flex align-items-start w-100 text-start">
                <span
                    className={`d-inline-flex align-items-center justify-content-center flex-shrink-0 me-2 item-icon${
                        options?.danger ? ' text-danger' : ''
                    }`}
                >
                    {icon}
                </span>
                <div className="flex-grow-1 min-w-0">
                    <div className={`fw-medium ${options?.danger ? 'text-danger' : ''}`}>{title}</div>
                    <small className="text-muted warehouse-team-menu-hint">{subtitle}</small>
                </div>
            </div>
        </Dropdown.Item>
    );

    const menu = (
        <Dropdown.Menu
            className="p-2 shadow-sm warehouse-team-menu"
            popperConfig={{ strategy: 'fixed' }}
            renderOnMount
        >
            {renderMenuItem(
                <SvgEdit />,
                t('setPopup.edit'),
                t('setPopup.editHint'),
                () => setEditSetId(productSet.ID),
            )}

            <Dropdown.Divider className="my-2" />

            {renderMenuItem(
                <SvgTrash />,
                t('setPopup.delete'),
                t('setPopup.deleteHint'),
                () => deleteProductSet(productSet, cbDeleteSet),
                { danger: true },
            )}
        </Dropdown.Menu>
    );

    return (
        <Dropdown align="end">
            <Dropdown.Toggle
                className="p-1 py-0"
                size="sm"
                variant="light-secondary"
                id={`dropdown-set-${productSet.ID}`}
            >
                <SolarIcon name="three-dots" />
            </Dropdown.Toggle>

            {typeof document !== 'undefined' ? createPortal(menu, document.body) : menu}
        </Dropdown>
    );
};

export default SetTableRowActions;
